import { Link } from "react-router-dom";
import { Reveal, SectionHead } from "@/components/site/motion";

const PROBLEMS = [
  {
    title: "The program isn’t producing revenue",
    text: "Partners are signed and activity is happening, but there’s little measurable partner-sourced pipeline, revenue, or ROI.",
  },
  {
    title: "There’s no clear partnership strategy",
    text: "The team hasn’t defined which partners matter, how to prioritize them, or what role partnerships should play in the company’s growth strategy.",
  },
  {
    title: "Partnerships are nobody’s full-time job",
    text: "A founder, sales leader, or other executive is managing partnerships on the side, leaving strategy, activation, and follow-through inconsistent.",
  },
  {
    title: "Growth has stalled",
    text: "The program has plateaued, key partners are underperforming, or relationships that once drove results are losing momentum.",
  },
  {
    title: "Leadership can’t see what’s working",
    text: "Attribution, KPIs, scorecards, and reporting are missing or unreliable, making it difficult to know where to invest, where to fix, and where to walk away.",
  },
  {
    title: "The company needs to build, or rebuild, the engine",
    text: "The business needs new partner channels, stronger reseller, ISV, and SI relationships, or a more disciplined operating model, and needs senior partnership leadership to build it.",
  },
];

export function Problems() {
  return (
    <section id="problems" className="section-tint" data-testid="problems-section">
      <div className="wrap">
        <SectionHead
          chapter="Chapter 01"
          kicker="What brings companies to me"
          title="Turning partnership potential into pipeline, revenue, and market expansion."
          lede="Strong partnerships can create new revenue, open markets, and accelerate growth, but only when the right strategy, operating model, and execution are in place. I help companies turn partnership potential into a more focused, scalable growth engine."
          testid="problems"
        />
        <div className="problem-grid">
          {PROBLEMS.map((p, i) => (
            <Reveal key={p.title} delay={(i % 2) * 0.08} y={18} className="problem-item" data-testid={`problem-item-${i}`}>
              <span className="problem-num">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h4 className="problem-title">{p.title}</h4>
                <p>{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const STAGES = [
  "Strategy & Opportunity",
  "Program Design & Economics",
  "Operating Model",
  "GTM & Activation",
  "Performance & Scale",
];

export function CapabilitiesTeaser() {
  return (
    <section id="capabilities" data-testid="capabilities-section">
      <div className="wrap">
        <SectionHead
          chapter="Chapter 02"
          kicker="Capabilities"
          title="From strategy to scale, I build, fix, and monetize partnership programs."
          lede="Whether a company is launching partnerships for the first time, trying to unlock more value from an existing ecosystem, or fixing a program that has stalled, I can step in across the full partnership lifecycle."
          testid="capabilities"
        />
        <div className="stage-flow" data-testid="stage-flow">
          {STAGES.map((s, i) => (
            <Reveal key={s} y={20} delay={i * 0.07} className="stage-flow-item" data-testid={`stage-flow-item-${i}`}>
              <span className="stage-num">{String(i + 1).padStart(2, "0")}</span>
              <span className="stage-label">{s}</span>
            </Reveal>
          ))}
        </div>
        <Reveal y={16}>
          <p className="stage-flow-note">Delivered through hands-on fractional or interim leadership.</p>
          <Link to="/services" className="btn btn-ghost" data-testid="capabilities-cta" style={{ marginTop: "1.2rem" }}>
            See the Full Approach <span className="btn-arrow">→</span>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
